import clsx from 'clsx';
import React, {useCallback, useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {BACK_BUTTON_ROUTES} from '@/config/routesConstants';
import {SUBSCRIPTION_HOME_ROUTES} from '@/config/subscriptionConstants';
import {SubscriptionVariant} from '@/types/subscription';
import BrandElement from '@/components/brand-element';
import Link from '@/components/link';
import {MenuPopularPaysLogo} from '@/components/unusual-icons';
import UserMenu from '@/components/user-menu';
import useGoBack from '@/hooks/use-go-back';
import useIsMobile from '@/hooks/use-is-mobile';
import styles from './BrandHeader.module.scss';
import BackButton from './child-components/back-button/BackButton';
import {useBreadcrumbsBack} from './child-components/breadcrumbs-header';
import useChildComponents from './useChildComponents';

export type BrandHeaderProps = {
  testID?: string;
  user: any;
  subscription: any;
};

function BrandHeader(props: BrandHeaderProps) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const childComponents = useChildComponents();

  const {testID = 'brand-header', user, subscription} = props;

  const routeChildComponent = useMemo(() => {
    return childComponents.find((childComponent) =>
      new RegExp(childComponent.matcher, 'i').test(location.pathname)
    );
  }, [childComponents, location.pathname]);

  const childComponent = useMemo(() => {
    const Component = routeChildComponent?.component;

    return (
      <div
        data-testid={`${testID}--${Component?.name}`}
        className={styles.childComponentContainer}
      >
        {Component && <Component />}
      </div>
    );
  }, [testID, routeChildComponent]);

  const hideBrandElement = Boolean(routeChildComponent?.hideBrandElement);

  const logoLinkOverride = useMemo(
    () =>
      SUBSCRIPTION_HOME_ROUTES[
        subscription.subscriptionType as SubscriptionVariant
      ],
    [subscription.subscriptionType]
  );

  const isBackButton =
    BACK_BUTTON_ROUTES.includes(location.pathname) ||
    Boolean(routeChildComponent?.showBackButton);

  const RightComponent = routeChildComponent?.rightComponent;

  const tryGoBack = useGoBack();
  const breadcrumbsBackDestination = useBreadcrumbsBack();

  const handleBreadcrumbBack = useCallback(() => {
    tryGoBack(breadcrumbsBackDestination ?? undefined, {
      forceFallbackUrl: true,
      replace: true,
    });
  }, [tryGoBack, breadcrumbsBackDestination]);

  return (
    <div
      className={clsx(styles.container, {
        [styles.containerNoBorder]: routeChildComponent?.hideBottomBorder,
      })}
      data-testid={testID}
    >
      <div
        className={clsx(styles.leftContainer, {
          [styles.leftContainerBreadcrumbs]:
            routeChildComponent?.showBackButton,
        })}
      >
        {isBackButton ? (
          <BackButton onBack={handleBreadcrumbBack} />
        ) : (
          <Link to={logoLinkOverride ?? '/'}>
            <MenuPopularPaysLogo height={32} />
          </Link>
        )}
        {isMobile ? null : childComponent}
      </div>
      <div className={styles.rightContainer}>
        {isMobile || hideBrandElement ? null : <BrandElement />}
        {RightComponent ? <RightComponent /> : null}
        {user.loaded && <UserMenu user={user} />}
      </div>
    </div>
  );
}

export default BrandHeader;
